import React from "react";
import ItemSelection from "../ItemSelection"; 
import ItemColors from "../ItemColors";
import Grids from "../options/grids/Grids";
import ShapeSelection from "../ShapeSelection";
import ToolsSelection from "../tools/ToolsSelection"; // Import ToolsSelection
import "./MenuComponent.css"; // Import the new CSS file

function MenuComponent({
  isFixed,
  handlePanel,
  typeName,
  addOption,
  addItem,
  handleAddAccessory,
  handleDoorsView,
  canvasHeight,
  projectName,
  isSelectedItem,
  updateColor,
  handleColorChange,
  eyePickerActiveColor,
  applyColorToSpecificItems,
  setIsOptionsPanelVisible,
  handleOptionsPanel,
  itemColor,
  setIsItemAdded,
  isCalibrating,
  windowURL,
  addTransum,
  setGlassToSearch,
  setStringToSearch,
  setStringToSearchSide,
  isTransum,
  setIsTransum,
  isSameType,
  isMixMatched,
  checkForSeletedItems,
  checkSelectedObjectType,
  toggleOptionsPanel,
  getSelectedItemType,
  activeObjectDetails,
  canvas,
  handleActionChange,
  copyItem,
  selectAll,
  cutItem,
  removeAllObjects2,
  pasteItem,
  copyAndPasteItem,
  objectfliphorizontal,
  objectflipvertical,
  drawGrid,
  unGroupItems,
  setModeType,
  setEraseMode,
  setDrawMode,
  changePerspective,
  calibrationDisplay,
  t,
  scaleFactor,
  showCalibrationOverlay,
  handleDropdownChange,
  selectedObject,
  onUseSelectedItemAsReferenceImage,
  onUseColorSwatchAsReferenceImage,
  onMakeItReal,
  isMakingItReal = false,
  makeItRealElapsedSeconds = 0
}) {
  const extractReferenceColor = (targetObject) => {
    if (!targetObject) {
      return null;
    }

    if (typeof targetObject.fill === 'string' && targetObject.fill.trim()) {
      return targetObject.fill.trim();
    }

    if (typeof targetObject.stroke === 'string' && targetObject.stroke.trim()) {
      return targetObject.stroke.trim();
    }

    const nestedObjects = Array.isArray(targetObject._objects) ? targetObject._objects : [];
    for (const nestedObject of nestedObjects) {
      if (typeof nestedObject?.fill === 'string' && nestedObject.fill.trim()) {
        return nestedObject.fill.trim();
      }

      if (typeof nestedObject?.stroke === 'string' && nestedObject.stroke.trim()) {
        return nestedObject.stroke.trim();
      }

      if (Array.isArray(nestedObject?.filters)) {
        const blendColorFilter = nestedObject.filters.find((filter) => filter?.type === 'BlendColor' && typeof filter?.color === 'string');
        if (blendColorFilter?.color) {
          return blendColorFilter.color.trim();
        }
      }
    }

    return null;
  };

  const normalizedSelectedItemType = String(selectedObject?.itemType || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  const hasReferenceColor = Boolean(extractReferenceColor(selectedObject));
  const canUseSelectedItemAsReferenceImage = ['window', 'windows', 'door', 'doors', 'garagedoor', 'garagedoors'].includes(normalizedSelectedItemType) || hasReferenceColor;
  const makeItRealLabel = isMakingItReal
    ? t("assistant.generatingFacadeAiElapsed", {
        defaultValue: "Make It Real {{count}}s",
        count: makeItRealElapsedSeconds,
      })
    : t("assistant.modeMakeItReal", "Make It Real");

  return (
    <div
      className={`menu-container ${isFixed ? "fixed" : "absolute"}`}
      data-onboarding="editor-menu"
    >
      <ItemSelection 
        handlePanel={handlePanel} 
        typeName={typeName}
        addItem={addItem}
        handleAddAccessory={handleAddAccessory}
        handleDoorsView={handleDoorsView}
        canvasHeight={canvasHeight}
      />
      {projectName && (
        <ItemColors
          updateColor={updateColor}
          pickerColor={eyePickerActiveColor}
          onColorChange={handleColorChange}
          applyColorToSpecificItems={applyColorToSpecificItems}
          onUseColorSwatchAsReferenceImage={onUseColorSwatchAsReferenceImage}
        />
      )}
      {isSelectedItem && (
        <Grids
          isSelectedItem={isSelectedItem}
          handleOptionsPanel={handleOptionsPanel}
          addOption={addOption}
          addTransum={addTransum}
          typeName={typeName}
          windowURL={windowURL}
          setGlassToSearch={setGlassToSearch}
          setStringToSearch={setStringToSearch}
          setStringToSearchSide={setStringToSearchSide}
          isTransum={isTransum}
          setIsTransum={setIsTransum}
          isSameType={isSameType}
          isMixMatched={isMixMatched}
          checkForSeletedItems={checkForSeletedItems}
          checkSelectedObjectType={checkSelectedObjectType}
          toggleOptionsPanel={toggleOptionsPanel}
          isInCalibrationMode={isCalibrating}
          getSelectedItemType={getSelectedItemType}
          activeObjectDetails={activeObjectDetails}
        />
      )}
      <ShapeSelection
        canvas={canvas}
        itemColor={itemColor}
        setIsOptionsPanelVisible={setIsOptionsPanelVisible}
        setIsItemAdded={setIsItemAdded}
      />

      {/* ToolsSelection */}
      {projectName && (
        <ToolsSelection
          changeAction={(e) => handleActionChange(e)}
          isObjectSelected={isSelectedItem}
          copyItem={() => copyItem(canvas)}
          selectAll={() => selectAll()}
          cutItem={() => cutItem(canvas)}
          removeAllObjects2={() => removeAllObjects2(canvas)}
          pasteItem={() => pasteItem(canvas)}
          copyAndPasteItem={() => copyAndPasteItem(canvas)}
          flipX={() => objectfliphorizontal(canvas)}
          flipY={() => objectflipvertical(canvas)}
          drawGrid={() => drawGrid(canvas, 50)}
          ungroupItems={() => unGroupItems(canvas)}
          setModeType={setModeType}
          setEraseMode={setEraseMode}
          setDrawMode={setDrawMode}
          changePerspective={() => changePerspective()}
        />
      )}

      {isSelectedItem && canUseSelectedItemAsReferenceImage && (
        <button
          type="button"
          className="btn menu-reference-action"
          onClick={onUseSelectedItemAsReferenceImage}
          title={t("details.useAsReferenceImage", "Use as reference image")}
        >
          <span className="menu-reference-action-icon" aria-hidden="true">+</span>
          <span className="menu-reference-action-label">{t("details.useAsReferenceImage", "Use as reference image")}</span>
        </button>
      )}

      {projectName && (
        <button
          type="button"
          className="btn menu-reference-action menu-make-real-action"
          onClick={onMakeItReal}
          title={makeItRealLabel}
          disabled={isMakingItReal}
        >
          <span className="menu-reference-action-icon" aria-hidden="true">
            {isMakingItReal ? "..." : t("assistant.aiShort", { defaultValue: "AI" })}
          </span>
          <span className="menu-reference-action-label">{makeItRealLabel}</span>
        </button>
      )}

      {/* Calibration Dropdown */}
      {/* {projectName && (
        <div className={"calibration"}>
          <DropdownButton
            title={
              <img
                className="addItemsButton"
                src="/icons/compast.png"
                height="40px"
                alt="Items"
                title="Calibration"
                style={{ filter: "invert(100%)" }}
              />
            }
            className="btn mt-1 btn-block pb-0 m-0"
            variant="primary"
            menuVariant="dark"
            size="sm"
            drop="bottom"
            autoClose="true"
            onSelect={handleDropdownChange}
          >
            <Dropdown.Item eventKey="removeDisplay">
              {!calibrationDisplay
                ? t("app-turnoncalibration")
                : scaleFactor === 1
                ? t("app-calibrationdisplaynull")
                : t("app-turnoffcalibration")}
            </Dropdown.Item>
            <Dropdown.Item eventKey="toggleCalibration">
              {scaleFactor < 1
                ? t("app-recalibration")
                : showCalibrationOverlay
                ? t("app-hidecalibration")
                : t("app-showcalibration")}
            </Dropdown.Item>
          </DropdownButton>
        </div>
      )} */}
    </div>
  );
}

export default MenuComponent;
